import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Tabs } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatTimestamp } from "./helpers";
import { RequestContent, ResultContent, StateContent } from "./SqlPanel";
import type { Activity, ActivityTab } from "./types";

const STATUS_STYLES: Record<Activity['status'], { node: string; card: string }> = {
  success: { node: "bg-[#EEF6E4] text-success border-[#CFE3B4]", card: "border-border" },
  error: { node: "bg-[#FDECEC] text-destructive border-[#F6C4C6]", card: "border-[#F6C4C6]" },
  warning: { node: "bg-sl-yellow-soft text-warning border-sl-yellow/60", card: "border-sl-yellow/60" },
  info: { node: "bg-[#E8F0FA] text-info border-[#C4D6EE]", card: "border-border" },
};

const TAB_LABELS: Record<ActivityTab, string> = { request: "Verzoek", result: "Resultaat", state: "State" };

function MetaItem({ label, value, tone }: { label: string; value: ReactNode; tone?: "success" | "danger" | "warning" }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-muted-foreground">{label}:</span>
      <span
        className={cn(
          "font-medium tabular-nums",
          tone === "success" && "text-success",
          tone === "danger" && "text-destructive",
          tone === "warning" && "text-warning",
          !tone && "text-foreground",
        )}
      >
        {value}
      </span>
    </span>
  );
}

function MetadataFooter({ metadata }: { metadata: NonNullable<Activity['metadata']> }) {
  const items = [
    metadata.tokens_in !== undefined && <MetaItem key="t" label="Tokens" value={`${metadata.tokens_in} → ${metadata.tokens_out || 0}`} />,
    metadata.duration_ms !== undefined && <MetaItem key="d" label="Duur" value={`${metadata.duration_ms} ms`} />,
    metadata.sql_found !== undefined && (
      <MetaItem key="f" label="SQL gevonden" value={metadata.sql_found ? "ja" : "nee"} tone={metadata.sql_found ? "success" : "danger"} />
    ),
    metadata.sql_valid !== undefined && (
      <MetaItem key="v" label="SQL geldig" value={metadata.sql_valid ? "ja" : "nee"} tone={metadata.sql_valid ? "success" : "warning"} />
    ),
    metadata.score !== undefined && <MetaItem key="s" label="Score" value={`${metadata.score}/5`} />,
    metadata.confidence && <MetaItem key="c" label="Zekerheid" value={metadata.confidence} />,
  ].filter(Boolean);
  if (items.length === 0) return null;
  return <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border bg-sl-grey-50 px-4 py-2 text-xs">{items}</div>;
}

export function TraceEventItem({ activity, isLast }: { activity: Activity; isLast: boolean }) {
  const [isExpanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<ActivityTab | null>(null);

  // Show tabs for request/result/state, but not for errors (errors go straight to error display)
  const hasRequestResult = !activity.error && Boolean(activity.request || activity.result || activity.state);
  // Errors are expandable even without tabs
  const isExpandable = hasRequestResult || Boolean(activity.error);
  // Default to 'result' tab if there's no request, otherwise 'request'
  const defaultTab: ActivityTab = activity.request ? 'request' : (activity.result ? 'result' : 'state');
  const currentTab = tab || defaultTab;
  const isNested = Boolean(activity.metadata?.isNested);
  const styles = STATUS_STYLES[activity.status] ?? STATUS_STYLES.info;

  const tabItems = (['request', 'result', 'state'] as ActivityTab[])
    .filter((t) => Boolean(activity[t]))
    .map((t) => ({ value: t, label: TAB_LABELS[t] }));

  return (
    <li className={cn("relative flex gap-3", isNested && "ml-6 sm:ml-10")}>
      {/* Timeline rail */}
      <div className="relative flex w-8 shrink-0 justify-center">
        {!isLast && <span className="absolute top-8 -bottom-3 w-px bg-border" aria-hidden />}
        <span className={cn("relative z-[1] mt-1 flex h-7 w-7 items-center justify-center rounded-full border", styles.node)}>
          {activity.icon}
        </span>
      </div>

      <div className={cn("mb-3 min-w-0 flex-1 rounded-md border bg-card", styles.card, isNested && "bg-card")}>
        <button
          type="button"
          className={cn(
            "flex w-full items-start gap-2 rounded-md px-3 py-2.5 text-left",
            isExpandable ? "cursor-pointer hover:bg-muted/60" : "cursor-default",
          )}
          onClick={() => isExpandable && setExpanded(!isExpanded)}
          aria-expanded={isExpandable ? isExpanded : undefined}
          tabIndex={isExpandable ? 0 : -1}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className={cn("text-sm font-semibold text-foreground", isNested && "font-mono text-[13px]")}>{activity.title}</span>
              <span className="font-mono text-[11px] text-muted-foreground">{formatTimestamp(activity.timestamp)}</span>
            </div>
            {/* Show condensed message for errors when collapsed */}
            {!isExpanded && activity.errorPreview && (
              <div className="mt-0.5 truncate text-xs text-destructive">{activity.errorPreview}</div>
            )}
          </div>
          {isExpandable && (
            <span className="mt-0.5 shrink-0 text-muted-foreground">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
          )}
        </button>

        {isExpanded && (
          <div className="border-t border-border">
            {hasRequestResult && (
              <>
                {tabItems.length > 1 && (
                  <div className="px-3 pt-3">
                    <Tabs value={currentTab} onChange={setTab} items={tabItems} />
                  </div>
                )}
                <div className="max-h-[420px] overflow-y-auto p-3">
                  {currentTab === 'request' && activity.request && <RequestContent request={activity.request} />}
                  {currentTab === 'result' && activity.result && <ResultContent result={activity.result} />}
                  {currentTab === 'state' && activity.state && <StateContent state={activity.state} />}
                </div>
                {activity.metadata && Object.keys(activity.metadata).length > 0 && <MetadataFooter metadata={activity.metadata} />}
              </>
            )}

            {/* Error display - only in expanded view */}
            {activity.error && (
              <div className="p-3">
                <pre className="max-h-[420px] overflow-auto rounded-md border border-[#F6C4C6] bg-[#FFF7F7] p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-destructive">
                  {activity.error}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
